import { IchiInsights } from 'common/models/Insights';
import { isFarmingV2ContractByVersion } from 'constants/contracts';
import { CryptoUtils, GAS_SETTINGS } from 'utils/cryptoUtils';
import { FarmUtils } from 'utils/farmUtils';
import { ClaimEvent } from 'xstate/claimEvent';
import { AppSyncFarm } from 'common/models/farm';
import { Nullable } from 'common/models/optional';
import { StorageUtils } from 'utils/storageUtils';
import { isUserRejectedError, isViemBaseError } from 'utils/errorUtils';
import { FARMING_V2 } from 'constants/constants';
import {
  Address,
  BaseError,
  TransactionReceipt,
  UserRejectedRequestError,
} from 'viem';
import { TokenUtils } from 'utils/tokenUtils';
import { readContract, writeContract } from '@wagmi/core';
import { genericFarmingV2 } from 'constants/abis/genericFarmingV2';

export namespace ClaimApi {
  export type ClaimResponse = {
    receipt?: TransactionReceipt;
    message?: string;
  };

  export type ClaimArgs = {
    poolId: number;
    farm: AppSyncFarm;
    accountAddress: Address;
    chainId: number;
  };

  export type GetClaimDataArgs = {
    chainId: number;
    poolId: number;
    farm: AppSyncFarm;
    accountAddress: Address;
  };

  export type GetClaimDataResponse = {
    ichiBalance: bigint;
  };

  export interface IClaim {
    claim(args: ClaimArgs): Promise<Nullable<StorageUtils.StoredTx>>;
    getClaimData(args: GetClaimDataArgs): Promise<GetClaimDataResponse>;
  }

  export class Claim implements IClaim {
    private static instance: IClaim;

    static claimFN = 'claim';
    static claimFNLabel = 'Claim';

    private constructor() {}

    public static getInstance(): IClaim {
      if (!Claim.instance) {
        Claim.instance = new Claim();
      }
      return Claim.instance;
    }

    async claim(args: ClaimArgs): Promise<Nullable<StorageUtils.StoredTx>> {
      let storedTx: Nullable<StorageUtils.StoredTx> = null;
      const contractContainer = TokenUtils.lookupFarmContract(
        args.chainId,
        FARMING_V2[args.chainId]
      );

      if (!contractContainer) {
        const message = 'No farm contract container provided';
        console.error(message);
        throw new Error(message);
      }

      const functionName = ClaimApi.Claim.claimFN;

      // TODO: Add the amount here
      const labels: StorageUtils.LabelsArg = {
        labels: {
          executing: 'Claiming...',
          success: 'Successfully claimed!',
          error: 'Error claiming!',
        },
      };

      // Really this should be moved to GraphQL and be a dynamically resolved field.
      const isGeneric = args.farm.poolId >= 20000;

      if (
        isFarmingV2ContractByVersion(
          args.chainId,
          contractContainer.version,
          contractContainer.token
        ) ||
        isGeneric
      ) {
        try {
          // Otherwise there is no tx in flight, execute contract call
          // console.debug(`Attempting to claim from pool: ${Number(args.poolId)}`);

          let tx: Nullable<Awaited<ReturnType<typeof writeContract>>> = null;
          let adjPoolId = 0n;

          if (isGeneric) {
            adjPoolId = BigInt(args.farm.farm.farmId);

            tx = await writeContract({
              address: args.farm.farm.farmAddress,
              abi: genericFarmingV2,
              functionName: 'harvest',
              args: [adjPoolId, args.accountAddress],
              gas: CryptoUtils.calculateGasMargin(
                BigInt(GAS_SETTINGS.claim),
                args.chainId
              ),
            });
          } else if (contractContainer.token) {
            adjPoolId = FarmUtils.getAdjustedPoolId(
              BigInt(args.poolId),
              args.chainId
            );

            tx = await writeContract({
              address: contractContainer.token.address,
              abi: contractContainer.abi,
              functionName: 'harvest',
              args: [adjPoolId, args.accountAddress],
              gas: CryptoUtils.calculateGasMargin(
                BigInt(GAS_SETTINGS.claim),
                args.chainId
              ),
            });
          }

          storedTx = await StorageUtils.storeTx({
            ...args,
            hash: tx?.hash as string,
            functionName,
            labels: labels.labels,
            value: args.poolId,
          });

          IchiInsights.trackEvent({
            event: ClaimEvent.CLAIM,
            properties: {
              ...args,
              poolId: `${args.poolId}`,
              adjPoolId: `${adjPoolId}`,
              handledAt: `claimApi:${Claim.claimFN}`,
              storedTx,
            },
          });
        } catch (err) {
          console.error(err);
          if (isUserRejectedError(err as BaseError)) {
            throw new Error((err as UserRejectedRequestError).shortMessage);
          }
          const error = isViemBaseError(err as BaseError)
            ? new Error((err as BaseError).shortMessage)
            : err;

          IchiInsights.trackException({
            error: error as Error,
            properties: {
              ...args,
              handledAt: `claimApi:${Claim.claimFN}`,
            },
          });
          throw error;
        }
      } else {
        const message = `Could not claim from pool: ${Number(
          args.poolId
        )}, no contract specified`;
        const error = new Error(message);
        IchiInsights.trackException({
          error,
          properties: {
            ...args,
            handledAt: `claimApi:${Claim.claimFN}`,
          },
        });
        throw error;
      }

      return storedTx;
    }

    async getClaimData(args: GetClaimDataArgs): Promise<GetClaimDataResponse> {
      const contractContainer = TokenUtils.lookupFarmContract(
        args.chainId,
        FARMING_V2[args.chainId]
      );

      if (!contractContainer) {
        const message = 'No farm contract container provided';
        console.error(message);
        throw new Error(message);
      }

      let rewardBalance = 0n;

      const isGeneric = args.farm.poolId >= 20000;

      if (
        isFarmingV2ContractByVersion(
          args.chainId,
          contractContainer.version,
          contractContainer.token
        ) ||
        isGeneric
      ) {
        try {
          let adjPoolId = 0n;

          if (isGeneric) {
            adjPoolId = BigInt(args.farm.farm.farmId);
            rewardBalance = await readContract({
              address: args.farm.farm.farmAddress,
              abi: genericFarmingV2,
              functionName: 'pendingReward',
              chainId: args.chainId,
              args: [adjPoolId, args.accountAddress],
            });
          } else if (contractContainer.token) {
            adjPoolId = FarmUtils.getAdjustedPoolId(
              BigInt(args.poolId),
              args.chainId
            );

            rewardBalance = await readContract({
              address: contractContainer.token.address,
              abi: contractContainer.abi,
              functionName: 'pendingIchi',
              chainId: args.chainId,
              args: [adjPoolId, args.accountAddress],
            });
          }
        } catch (error) {
          const message = `Could not get claim data for pool: ${args.poolId}`;
          console.warn(message, error);
          IchiInsights.trackException({
            error: error as Error,
            properties: {
              ...args,
              handledAt: 'claimApi:getClaimData',
            },
          });
        }
      }

      const result: GetClaimDataResponse = {
        ichiBalance: rewardBalance,
      };
      console.debug('claim data', result);
      return result;
    }
  }
}
